/* eslint-disable complexity */
/* eslint-disable max-len */

/**
 * @file 页面侧边导航 /content 布局
 * @author
 */

import React, {useCallback, useMemo, useEffect, useRef, useState} from 'react';
import {App, Layout, Result} from 'antd';
import {Outlet, useNavigate, useLocation} from 'react-router-dom';
import {observer} from 'mobx-react-lite';
import {flowResult} from 'mobx';
import cx from 'classnames';

import {defaultSelectJobUtil} from '@/modules/selectJobUtil';
import {jobIdSyncService} from '@/utils/JobIdSyncService';
import {useRequestErrorHandler} from '@/utils/errorHandler';
import {messageManager} from '@/utils/errorHandler/MessageManager';
import {RootStore, ProjectsListStore, TaskRunningStore, JobStore} from '@/stores';
import FullPageSpin from '@/components/FullPageSpin';
import CustomSider from '@/components/CustomSider';
import CollapsedBtn from '@/components/CollapsedBtn';
import {Menus} from '@/modules/SiderModules';
import {BillingModalHost} from '@/modules/BillingModules';
import {MigrationBanner, useMigrationBanner} from '@/modules/MigrationModules';
import InitialEmptyPage from '@/pages/InitialPage/empty';
import styles from './sider.module.less';

const {Content} = Layout;

interface SiderLayoutProps {
    rootStore: RootStore;
}

const SiderLayout: React.FC<SiderLayoutProps> = ({rootStore}) => {
    const {siderNavigationStore, userStore} = rootStore;
    const collapsed = siderNavigationStore.collapsed;
    const fetchListFlag = useRef<boolean>(false);
    const {message} = App.useApp();
    const {handleError} = useRequestErrorHandler();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [listInitialized, setListInitialized] = useState(false);

    /** 管理平台下线迁移通栏 */
    const migrationBanner = useMigrationBanner(userStore);

    /** 与任务无关的页面，不需要任务选中状态和自动跳转 */
    const isBillingPage = location.pathname === '/billing';
    const isTaskPage = ['/index', '/taskRunning'].includes(location.pathname);

    /** 地址栏上的 jobId */
    const savedJobId = useMemo(
        () => jobIdSyncService.getJobIdFromUrl() || userStore.templateJobId,
        [location.search, userStore.templateJobId]
    );

    // 监听 templateJobId 变化，同步地址栏
    useEffect(
        () => {
            if (userStore.templateJobId) {
                jobIdSyncService.setJobId(userStore.templateJobId);
            }
        },
        [userStore.templateJobId]
    );

    // 注入 navigate 到 jobIdSyncService，使其能同步更新 URL
    useEffect(
        () => {
            jobIdSyncService.setNavigate(navigate);
        },
        [navigate]
    );
    const store = useMemo(
        () => {
            const projectsListStore = new ProjectsListStore(userStore.userId);
            const taskRunningStore = new TaskRunningStore('', projectsListStore.jobsListStore); // 初始化
            return {
                projectsListStore,
                rootStore,
                taskRunningStore,
                billingStore: rootStore.billingStore
            };
        },
        []
    );

    const {jobsListStore} = store.projectsListStore;

    /**
     * 检查并处理任务状态变化
     */
    const checkAndHandleJobStatusChange = useCallback(
        async () => {
            const selectedJobId = jobsListStore.selectedJobId;
            const currentJobDetail = jobsListStore.selectedJobDetail;

            if (!selectedJobId || !currentJobDetail) {
                return false;
            }

            const latestJobInfo = jobsListStore.getJobListInfo(selectedJobId);

            if (!latestJobInfo) {
                return false;
            }

            const statusChanged =
                latestJobInfo.status !== currentJobDetail.status
                || latestJobInfo.stage_status !== currentJobDetail.stage_status;

            if (statusChanged) {

                fetchListFlag.current = true;
                try {
                    await flowResult(jobsListStore.setDetailsUpdate());
                } finally {
                    fetchListFlag.current = false;
                }

                return true;
            }

            return false;
        },
        [jobsListStore]
    );

    /**
     * 获取任务列表（不触发 detail）
     */
    const getJobsList = useCallback(
        async () => {
            try {
                fetchListFlag.current = true;
                await flowResult(jobsListStore.setDetailsUpdate());
                setListInitialized(true);
            } catch (error: unknown) {
                handleError(error);
            } finally {
                fetchListFlag.current = false;
            }
        },
        [jobsListStore, handleError]
    );

    // 组件卸载时停止轮询
    useEffect(
        () => {
            // 清理函数：组件卸载时执行
            return () => {
                store.taskRunningStore.stopPolling();
                store.taskRunningStore.initTaskRunningStore('');
            };
        },
        []
    ); // 空依赖数组，只在挂载和卸载时执行


    /**
     * 定时刷新任务列表
     */
    useEffect(
        () => {
            const initList = async () => {
                if (!fetchListFlag.current) {
                    await getJobsList();
                }
            };

            void initList();

            const timer = setInterval(async () => {
                try {
                    if (!fetchListFlag.current) {
                        await flowResult(jobsListStore.fetchList());
                        await checkAndHandleJobStatusChange();
                    }
                } catch (error) {
                    handleError(error);
                }
            }, 5000);

            return () => {
                clearInterval(timer);
            };
        },
        [getJobsList, jobsListStore, checkAndHandleJobStatusChange, handleError]
    );

    const getNewJobDetail = async () => {
        // 等待 list 初始化完成后再执行
        if (!listInitialized) {
            return;
        }

        if (!savedJobId && !isTaskPage) {
            return;
        }

        // 确定要使用的 job：
        // 1. 如果地址栏有 jobId 且在 list 中，使用它
        // 2. 如果地址栏没有 jobId 且在任务页，使用 list 第一个
        // 3. 如果地址栏上的 jobId 不在 list 中，不自动兜底，交给资源异常页提示
        let job = savedJobId ? jobsListStore.jobsList.find(j => j.job_id === savedJobId) : null;

        if (!savedJobId && jobsListStore.jobsList.length > 0) {
            job = jobsListStore.jobsList[0];
            jobIdSyncService.setJobId(job.job_id);
        }

        if (savedJobId && !job) {
            return;
        }

        // 只有当有任务时才获取详情
        if (job) {
            await defaultSelectJobUtil(store, job, setLoading);

            // 非任务页不做自动跳转
            if (!isTaskPage) {
                return;
            }

            // 数据加载完成后，执行一次导航
            const selectedJobDetail = jobsListStore.selectedJobDetail;
            if (selectedJobDetail instanceof JobStore) {
                const targetPath = !store.taskRunningStore.taskExecutionStore.isInitialingPage && !selectedJobDetail.isShowInitialingPage
                    ? `/taskRunning${location.search}`
                    : `/index${location.search}`;
                if (location.pathname !== targetPath.split('?')[0]) {
                    message.destroy();
                    messageManager.destroyAll();
                    navigate(targetPath);
                }
            }
        }
    };

    const isInitializing = useRef(false);

    useEffect(
        () => {
            if (isBillingPage) {
                return;
            }
            // 等待以下条件都满足：
            // 1. list 已初始化完成
            // 2. 当前没有在初始化中
            // 3. 如果 savedJobId 存在，确保 taskRunningStore 还没有初始化这个 job
            if (!listInitialized || isInitializing.current) {
                return;
            }

            // 如果 savedJobId 存在且已经初始化过了，跳过
            if (savedJobId && store.taskRunningStore.job_id === savedJobId) {
                return;
            }

            isInitializing.current = true;
            setLoading(true);

            const initJob = async () => {
                try {
                    if (savedJobId && jobsListStore.jobsList.some(job => job.job_id === savedJobId)) {
                        store.taskRunningStore.initTaskRunningStore(savedJobId);
                    }
                    await getNewJobDetail();
                } finally {
                    isInitializing.current = false;
                    setLoading(false);
                }
            };

            initJob();
        },
        [
            savedJobId,
            listInitialized
        ]
    );

    const hasInvalidUrlJobId = useMemo(
        () => {
            return !!savedJobId
                && listInitialized
                && jobsListStore.jobsList.length > 0
                && !jobsListStore.jobsList.some(job => job.job_id === savedJobId);
        },
        [savedJobId, listInitialized, jobsListStore.jobsList]
    );

    /** 监听任务状态变化，动态切换页面 */
    useEffect(
        () => {
            const {taskRunningStore} = store;
            const {selectedJobDetail, selectedJobId} = jobsListStore;

            // 非任务页不响应任务状态变化，不自动跳转
            if (!isTaskPage) {
                return;
            }

            if (hasInvalidUrlJobId) {
                return;
            }

            // 忽略初始化阶段的状态变化
            if (isInitializing.current) {
                return;
            }

            if (!selectedJobId || !(selectedJobDetail instanceof JobStore)) {
                return;
            }

            // 检查是否在查看模式，如果是则不自动跳转（由 InitialPage 的倒计时处理）
            const stepStore = selectedJobDetail.stepStore;
            if (stepStore?.isViewMode && stepStore?.currentStep === 3) {
                return;
            }

            const targetPath = !taskRunningStore.taskExecutionStore.isInitialingPage && !selectedJobDetail.isShowInitialingPage
                ? `/taskRunning${location.search}`
                : `/index${location.search}`;

            // 只有目标路径和当前路径不同时才跳转
            if (location.pathname !== targetPath.split('?')[0]) {
                messageManager.destroyAll();
                message.destroy();
                navigate(targetPath);
            }
        },
        [
            jobsListStore.selectedJobDetail.status,
            jobsListStore.selectedJobDetail.stage_status,
            store.taskRunningStore.taskExecutionStore.jobRunningPage,
            jobsListStore,
            store,
            isTaskPage,
            hasInvalidUrlJobId,
            message,
            location.pathname,
            location.search,
            navigate
        ]
    );
    /** 侧边导航折叠 */
    useEffect(
        () => {
            // 进入页面时
            siderNavigationStore.enterTaskRunningPage();
            return () => {
                // 离开页面时
                siderNavigationStore.exitTaskRunningPage();
            };
        },
        [siderNavigationStore]
    );

    useEffect(
        () => {
            if (userStore.is_first_login && !userStore.isShowGuideModal && userStore.isFirstShowGuideModal) {
                userStore.setShowGuideModal(true);
                userStore.setIsFirstShowGuideModal(false);
            }
        },
        [userStore.is_first_login, userStore.isShowGuideModal, userStore.isFirstShowGuideModal, userStore]
    );

    /** 充值页：停止 taskRunning 轮询；离开充值页：任务进行中则恢复轮询 */
    useEffect(
        () => {
            const {taskRunningStore} = store;
            if (isBillingPage) {
                taskRunningStore.stopPolling();
            } else if (!taskRunningStore.isTerminatedOrCompletedFiled) {
                taskRunningStore.startPolling();
            }
        },
        [isBillingPage, store]
    );

    // 充值页不阻塞渲染（后台仍在初始化任务数据）
    if (loading && !isBillingPage) {
        return <FullPageSpin />;
    }

    const {jobsList} = jobsListStore;

    /** 渲染页面内容 */
    const renderContent = () => {
        // 充值页直接渲染，不依赖任务选中状态
        if (isBillingPage) {
            return <Outlet context={{store}} />;
        }

        // 没有任务时显示空页面
        if (!jobsList.length) {
            return <InitialEmptyPage store={store} />;
        }

        // 地址栏 jobId 不存在于列表中时，提示用户从左侧任务列表重新选择
        if (hasInvalidUrlJobId) {
            return (
                <div className={styles.resourceExceptionWrapper}>
                    {collapsed && <CollapsedBtn store={siderNavigationStore} className={styles.resourceExceptionToggleButton} />}
                    <Result
                        icon={null}
                        type="primary"
                        title="资源异常"
                        subTitle="当前任务不存在或暂无权限访问，请点击左侧任务列表中的任务或创建新任务重新进入。"
                    />
                </div>
            );
        }

        if (jobsListStore.selectedJobId) {
            return <Outlet context={{store}} />;
        }

        return <FullPageSpin />;
    };

    return (
        <Layout className={styles.mainLayout}>
            <CustomSider store={siderNavigationStore}>
                <Menus store={store} />
            </CustomSider>
            {/* 页面内容 */}
            <Layout className={cx(
                styles.contentLayout,
                {[styles.collapsedContent]: collapsed},
                {[styles.openContent]: !collapsed},
                {[styles.withMigrationBanner]: migrationBanner.visible}
            )}
            >
                <MigrationBanner {...migrationBanner} />
                <Content>
                    {renderContent()}
                </Content>
                <BillingModalHost store={store} />
            </Layout>
        </Layout>
    );
};

export default observer(SiderLayout);
